import { appConfigurationQueryOptions } from "@/api/app-configuration.api";
import {
  CampaignRun,
  getCampaignRunsQueryOptions,
  simpleCampaignsQueryOptions,
  useSimpleCampaigns,
} from "@/api/campaign.api";
import { Button } from "@/components/generic/Button";
import { ErrorPage } from "@/components/layout/ErrorPage";
import { Meta } from "@/components/layout/Meta";
import { TrialCTAModal } from "@/components/trial/TrialCTAModal";
import {
  essentialsCreditsText,
  getTrialPeriod,
} from "@/hooks/use-trial-period";
import { AppointmentRemindersProvider } from "@/providers/AppointmentRemindersProvider";
import { ThemeContext, themes } from "@/providers/theme-context";
import { initAmplitude } from "@/services/amplitude.service";
import { authQueryOptions, isAdmin } from "@/services/auth.service";
import {
  EditorLockContext,
  useEditorLockService,
} from "@/services/editor-lock.service";
import { expectedRunsByCampaignIdAtom } from "@/services/push-message-fallback.service";
import { usePushMessageLeadsService } from "@/services/push-message-leads";
import {
  RuntimeHistoryContext,
  useRouterHistoryService,
} from "@/services/router-history.service";
import {
  LassoPushMessageListener,
  usePushMessageListener,
} from "@/services/socket.service";
import { Feature } from "@/types/auth";
import { ConfigurationRequest } from "@/types/campaign";
import { queryClient } from "@/utils/api";
import { type QueryClient, type QueryKey } from "@tanstack/react-query";

import {
  CatchBoundary,
  createRootRouteWithContext,
  Outlet,
  redirect,
  retainSearchParams,
  useBlocker,
  useMatch,
  useRouter,
  type NavigateOptions,
  type RegisteredRouter,
  type ToPathOption,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useSetAtom } from "jotai";
import { Palette } from "lucide-react";
import {
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { Button as RACButton, RouterProvider } from "react-aria-components";
import { ProductFruits } from "react-product-fruits";
import { toast } from "sonner";
import z from "zod";

const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import("@tanstack/react-query-devtools").then((m) => ({
        default: m.ReactQueryDevtools,
      })),
    )
  : () => null;
const TanStackRouterDevtools = import.meta.env.DEV
  ? lazy(() =>
      import("@tanstack/react-router-devtools").then((m) => ({
        default: m.TanStackRouterDevtools,
      })),
    )
  : () => null;
const DebugUtils = import.meta.env.DEV
  ? lazy(() =>
      import("@/components/debug/DebugUtils").then((m) => ({
        default: m.DebugUtils,
      })),
    )
  : () => null;

declare module "react-aria-components" {
  interface RouterConfig {
    href: ToPathOption<RegisteredRouter, "/", "/"> | ({} & string);
    routerOptions: Omit<NavigateOptions, "to" | "from">;
  }
}

function Component() {
  const router = useRouter();
  const { auth, appConfiguration } = Route.useRouteContext();

  const runtimeHistory = useRouterHistoryService();

  const { canPurchaseBuffer, canUpgradeCreditTier } = appConfiguration.features;

  const userInfo = useMemo(() => {
    return {
      username: auth.user.id, // Using user ID as unique identifier
      email: auth.user.email,
      firstname: auth.user.name.split(" ")[0] || "",
      lastname: auth.user.name.split(" ").slice(1).join(" ") || "",
      signUpAt: auth.user.created,
      role: isAdmin(auth.user) ? "Admin" : "User",
      props: {
        trial: appConfiguration.subscription.trial ? "Yes" : "No",
        plan: auth.featuresSet.has(Feature.LassoSalesPro) ? "Pro" : "Standard",
        organizationId: auth.user.organization.lassoOrganizationId,
        organizationName: auth.user.organization.name,
      },
    };
  }, [auth, appConfiguration]);

  const { setTheme, setVariant, currentTheme, variant } =
    useContext(ThemeContext);

  const cycleTheme = () => {
    // Get the current theme's variants
    const currentThemeVariants = themes[currentTheme];

    // Find the next variant in the current theme
    const currentIndex = currentThemeVariants.indexOf(variant);
    const nextIndex = (currentIndex + 1) % currentThemeVariants.length;
    const nextVariant = currentThemeVariants[nextIndex];

    // If we've cycled through all variants in the current theme
    if (nextIndex === 0) {
      // Switch to the other theme and use its first variant
      const nextTheme = currentTheme === "dark" ? "light" : "dark";
      setTheme(nextTheme);
      setVariant(themes[nextTheme][0]);
    } else {
      // Just change the variant within the current theme
      setVariant(nextVariant);
    }
  };

  useEffect(() => {
    const cleanup = initAmplitude(auth.user, appConfiguration, auth.claims);
    return cleanup;
  }, []);

  const simpleCampaigns = useSimpleCampaigns();
  const campaignNamesById = useMemo(
    () =>
      new Map(simpleCampaigns?.map((campaign) => [campaign.id, campaign.name])),
    [simpleCampaigns],
  );

  const routeCampaignId = useMatch({
    from: "/_dashboard/campaigns/$id",
    select: (match) => match.params.id,
    shouldThrow: false,
  });

  const isNewCampaign = useMatch({
    from: "/_dashboard/campaigns/setup",
    select(match) {
      if (!match) return false;
      return true;
    },
    shouldThrow: false,
  });

  usePushMessageLeadsService();

  const setExpectedRunsByCampaignId = useSetAtom(expectedRunsByCampaignIdAtom);

  usePushMessageListener(
    "CampaignRunStarted",
    useCallback<LassoPushMessageListener>(
      ({ message }) => {
        const campaignRun = message as CampaignRun;

        // Remove the campaign from the expected runs map, since we now know it's running.
        setExpectedRunsByCampaignId((prev) => {
          if (!prev.has(campaignRun.campaignId)) return prev;
          const newMap = new Map(prev);
          newMap.delete(campaignRun.campaignId);
          return newMap;
        });

        const campaignRunsQueryKey = getCampaignRunsQueryOptions(
          campaignRun.campaignId,
        ).queryKey;

        // Update the campaign runs cache if needed.
        queryClient.setQueryData(campaignRunsQueryKey, (prev) => {
          if (!prev) return prev;
          // Ignore if this run is older than the currently cached latest run.
          if (prev?.latestRun && campaignRun.id <= prev.latestRun.id)
            return prev;
          return {
            latestRun: campaignRun,
            totalRuns: prev.totalRuns + 1,
          };
        });

        // Invalidate the campaign runs query.
        queryClient.invalidateQueries({ queryKey: campaignRunsQueryKey });
      },
      [setExpectedRunsByCampaignId],
    ),
  );

  usePushMessageListener(
    "CampaignRunFinished",
    useCallback<LassoPushMessageListener>(
      ({ message }) => {
        const campaignRun = message as CampaignRun;

        // Remove the campaign from the expected runs map, since we now know it was running.
        setExpectedRunsByCampaignId((prev) => {
          if (!prev.has(campaignRun.campaignId)) return prev;
          const newMap = new Map(prev);
          newMap.delete(campaignRun.campaignId);
          return newMap;
        });

        const campaignRunsQueryKey = getCampaignRunsQueryOptions(
          campaignRun.campaignId,
        ).queryKey;

        // Update the campaign runs cache if needed.
        queryClient.setQueryData(campaignRunsQueryKey, (prev) => {
          if (prev?.latestRun?.id !== campaignRun.id) return prev;
          return {
            ...prev,
            latestRun: campaignRun,
          };
        });

        // Invalidate the campaign runs query.
        queryClient.invalidateQueries({ queryKey: campaignRunsQueryKey });

        // Update credits.
        queryClient.invalidateQueries({ queryKey: ["credits"] });

        // Invalidate the query used for the edit campaign sidebar.
        queryClient.invalidateQueries({
          queryKey: ["leads", { pageSize: 1, campaignId: [routeCampaignId] }],
        });

        // Invalidate campaign-leads-overview queries that include this campaign.
        queryClient.invalidateQueries({
          queryKey: ["campaign-leads-overview"],
          predicate: ({ queryKey }) => {
            const [, ids = null] = queryKey;
            if (Array.isArray(ids)) {
              return ids.includes(routeCampaignId);
            }
            return false;
          },
        });

        // The run delivered leads, so the campaign's counts and max leads are outdated.
        const isCampaignCounts = ({ queryKey }: { queryKey: QueryKey }) =>
          queryKey[0] === "counts" &&
          (queryKey[1] as ConfigurationRequest | undefined)?.campaignId ===
            campaignRun.campaignId;
        const maxLeadsQueryKey = [
          "campaignRunMaxLeads",
          campaignRun.campaignId,
        ];
        // Inactive ones (e.g. edit pages) don't refetch on mount, so drop them to load again when opened.
        queryClient.removeQueries({
          predicate: isCampaignCounts,
          type: "inactive",
        });
        queryClient.removeQueries({
          queryKey: maxLeadsQueryKey,
          type: "inactive",
        });
        if (campaignRun.campaignId === routeCampaignId) {
          void Promise.all([
            queryClient.invalidateQueries({ predicate: isCampaignCounts }),
            queryClient.invalidateQueries({ queryKey: maxLeadsQueryKey }),
          ]).then(() =>
            // Rerun the campaign route's beforeLoad, which passes the fresh counts to /run/max.
            router.invalidate(),
          );
        }

        // User is already viewing this campaign.
        if (campaignRun.campaignId === routeCampaignId) return;

        const campaignName = campaignNamesById.get(campaignRun.campaignId);
        if (!campaignName) return;

        toast(`Leads leveret til kampagnen "${campaignName}"`, {
          action: {
            label: "Gå til kampagne",
            onClick: () => {
              router.navigate({
                from: Route.fullPath,
                to: "/campaigns/$id",
                params: { id: campaignRun.campaignId },
              });
            },
            actionButtonStyle: {
              backgroundColor: "linear-gradient(var(--theme-gradient))",
            },
          },
        });
      },
      [setExpectedRunsByCampaignId, routeCampaignId, campaignNamesById, router],
    ),
  );

  const editorLockState = useEditorLockService();

  const buyCreditsBlocker = useBlocker({
    shouldBlockFn: ({ current, next }) => {
      const isBuyCredits = (pathname: string) =>
        pathname.includes("/buy-credits/") || pathname.endsWith("/buy-credits");

      if (
        isBuyCredits(next.pathname) &&
        !isBuyCredits(current.pathname) &&
        !canPurchaseBuffer &&
        !canUpgradeCreditTier
      )
        return true;
      return false;
    },
    enableBeforeUnload: false,
    withResolver: true,
  });

  return (
    <Meta>
      <RouterProvider
        navigate={(to, options) =>
          router.navigate({
            ...options,
            to: to as ToPathOption<RegisteredRouter, "/", "/">,
          })
        }
        useHref={(to) => router.buildLocation({ to }).href}
      >
        <EditorLockContext.Provider value={editorLockState}>
          <RuntimeHistoryContext.Provider value={runtimeHistory}>
            <div
              id="main-scrollable-area"
              className="text-theme-on-background relative flex min-h-full flex-col gap-y-3 p-3.5 has-[#error-page]:p-0 xl:p-7"
            >
              <CatchBoundary
                getResetKey={() => "reset"}
                onCatch={(error) => {
                  if (import.meta.env.DEV) {
                    throw error;
                  }
                }}
                errorComponent={({ reset }) => (
                  <ErrorPage
                    icon="cloud-alert"
                    title="Der opstod en fejl"
                    message="Der opstod desværre en uventet fejl. Vores teknikere har fået besked og arbejder på sagen"
                    actions={
                      <>
                        {import.meta.env.DEV && (
                          <Button
                            intent="primary"
                            onClick={() => {
                              reset();
                            }}
                            className="mt-4"
                          >
                            Prøv igen
                          </Button>
                        )}
                        <Button
                          intent="primary"
                          onClick={() => {
                            reset();
                            window.history.back();
                          }}
                          className="mt-4"
                        >
                          Gå tilbage
                        </Button>
                      </>
                    }
                  />
                )}
              >
                <AppointmentRemindersProvider>
                  <Outlet />
                </AppointmentRemindersProvider>
              </CatchBoundary>
            </div>

            {import.meta.env.DEV &&
              import.meta.env.VITE_SHOW_DEBUG_TOOLS !== "false" && (
                <div className="fixed bottom-3 left-3 z-50 flex items-center justify-center gap-x-4 rounded-full">
                  <RACButton
                    onPress={cycleTheme}
                    className="cursor-pointer rounded-full bg-white p-2 shadow dark:bg-black"
                  >
                    <Palette size={24} className="text-theme-on-background" />
                  </RACButton>

                  <Suspense fallback={null}>
                    {isNewCampaign && (
                      <DebugUtils
                        className="relative right-auto bottom-auto"
                        isNewSetup
                      />
                    )}
                  </Suspense>

                  <Suspense fallback={null}>
                    <TanStackRouterDevtools
                      position="bottom-right"
                      toggleButtonProps={{
                        style: { right: "4.5rem", bottom: "1.2rem" },
                      }}
                    />
                  </Suspense>
                  <Suspense fallback={null}>
                    <ReactQueryDevtools buttonPosition="bottom-right" />
                  </Suspense>
                </div>
              )}

            {!auth.claims.impersonating && import.meta.env.PROD && (
              <ProductFruits
                workspaceCode="PqpPyDcmhpbLYL17"
                language="da"
                user={userInfo}
                config={{
                  hideInAppCenterLauncher: true,
                }}
              />
            )}
            {buyCreditsBlocker.status === "blocked" && (
              <TrialCTAModal
                isOpen
                onOpenChange={() => buyCreditsBlocker.reset()}
                title={essentialsCreditsText}
              />
            )}
          </RuntimeHistoryContext.Provider>
        </EditorLockContext.Provider>
      </RouterProvider>
    </Meta>
  );
}

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  component: Component,
  validateSearch: zodValidator(
    z.object({
      internal_trial_test: z.iso.date().optional(),
    }),
  ),
  search: {
    middlewares: [retainSearchParams(["internal_trial_test"])],
  },
  beforeLoad: async ({ context, location, matches }) => {
    const auth = await context.queryClient.ensureQueryData(authQueryOptions);
    const { user } = auth;

    const [appConfiguration] = await Promise.all([
      context.queryClient.ensureQueryData(appConfigurationQueryOptions),
      context.queryClient.ensureQueryData(simpleCampaignsQueryOptions),
    ]);

    const trialPeriod = getTrialPeriod(appConfiguration);

    if (
      !appConfiguration.features.canPurchaseBuffer &&
      !appConfiguration.features.canUpgradeCreditTier &&
      (location.pathname.includes("/buy-credits/") ||
        location.pathname.endsWith("/buy-credits"))
    ) {
      const buyCreditsMatchIdx = matches.findIndex(
        (m): m is typeof m & { routeId: `${string}/buy-credits` } =>
          m.routeId.endsWith("/buy-credits"),
      );
      throw redirect({
        to: matches[buyCreditsMatchIdx - 1].pathname,
        replace: true,
      });
    }

    return { auth, user, appConfiguration, trialPeriod };
  },
});
